/**
 * Runtime configuration key constants.
 *
 * Centralises the string literals used with `getConfigValue()` so that:
 *  - Typos are caught at compile time rather than silently returning undefined.
 *  - Refactoring a key name only requires a single change here.
 *  - New keys are easy to discover via IDE autocomplete.
 *
 * Add new keys here as they are introduced. Existing callers that still use
 * inline strings can be migrated incrementally.
 */

// ── Google Places geo-restriction (Map search box in trip booking) ──────────
// Used by RoundTripDetails (inline map search) and TripLocationMapViewModal (modal search).
// Mirrors the same three config keys used by mobile mobileApi to bias the Google Places
// Autocomplete dropdown toward the agency service area.
// When all three are configured, a LatLngBounds bounding box is computed and passed as
// the `bounds` option with `strictBounds: false` — results are biased but never blocked.
export const CONFIG_KEY_PLACES_CENTER_LAT = 'component.mobileApi.googlePlacesCenterLatitude' as const;
export const CONFIG_KEY_PLACES_CENTER_LNG = 'component.mobileApi.googlePlacesCenterLongitude' as const;
/** Radius in metres used to compute the Places Autocomplete bounding box. */
export const CONFIG_KEY_PLACES_RADIUS_METERS = 'component.mobileApi.googlePlacesRadius' as const;

// ── Provider based trip fare billing ────────────────────────────────────────
/** Enables provider selection on billing rules used for provider based fares. */
export const CONFIG_KEY_PROVIDER_BASED_TRIP_FARE_ENABLED = 'component.billing.providerBasedTripFare.enabled' as const;

// ── Ecolane-FASTER integration ──────────────────────────────────────────────
/**
 * ECOPD-17640 — per-agency feature flag gating ALL Ecolane-FASTER
 * operational-status UI (Vehicles operational columns/filter/banner/export,
 * Vehicle Details "Operational Status" sections, Run Status non-operational
 * warning icon and the Substitute Vehicle flow). Absent/false => every FASTER
 * surface hidden and its API calls suppressed (default OFF).
 */
export const CONFIG_KEY_FASTER_ENABLED = 'component.faster.enabled' as const;

// ── Rider App (TTAuth) migration ────────────────────────────────────────────
/**
 * ECOPD-10071 — per-agency cutover of self-service riders to the new Rider App
 * (TTAuth). When on, new self-service accounts are invited through TTAuth (it
 * sends the onboarding email) and the legacy "Reset password" actions are
 * hidden. Absent/false => legacy behaviour (default OFF).
 */
export const CONFIG_KEY_SELFSERVICE_TTAUTH_ENABLED = 'reactnative.selfservice.ttauth.enabled' as const;

// ── Dashboard widget thresholds ─────────────────────────────────────────────
/** Minutes threshold for the Stop OTP by Driver widget (W-13). Default: 5 min. */
export const CONFIG_KEY_STOP_OTP_MISS_THRESHOLD = 'dashboard.widget.stop_otp.miss' as const;
/** Yellow-band upper boundary in minutes for OTP severity coloring. Default: 15 min. */
export const CONFIG_KEY_OTP_YELLOW_THRESHOLD = 'dashboard.widget.otp.yellow' as const;
/** Red-band lower boundary in minutes for OTP severity coloring. Default: same as yellow + 1. */
export const CONFIG_KEY_OTP_RED_THRESHOLD = 'dashboard.widget.otp.red' as const;

// ── Trip export ─────────────────────────────────────────────────────────────
/**
 * ECOPD-18281 — K3 double export control (drt_ui ConfigKey::CONTROL_TRIP_RE_EXPORT_ENABLED).
 * When true, Archive Trip Export offers "Prevent trip re-export" / "Mark trip exported",
 * sent to K3 export/start as prevent_trip_re_export / mark_trip_exported.
 */
export const CONFIG_KEY_CONTROL_TRIP_RE_EXPORT_ENABLED = 'component.export.controlTripReExport.enabled' as const;
